import { Box, Stack, ThemeProvider, Typography, useTheme } from '@mui/material';
import {
  AppLayout,
  Avatar,
  Button,
  Dialog,
  Grid,
  HeaderMobile,
  Icon,
  MenuList,
  MenuSelect,
  NavItem,
  NavList,
  SideBar,
  ANNOUNCEMENT_TYPE,
  DISMISS_BEHAVIOR,
} from 'bs-unified-ui';
import clsx from 'clsx';
import { useMemo } from 'react';
import { MOCK_NAVIGATION_DATA } from '~/constants';

type Props = {
  open: boolean;
  onClose: () => void;
  announcement: any;
};

const Preview = ({ open, onClose, announcement }: Props) => {
  const theme = useTheme();

  const renderBottom = useMemo(
    () =>
      MOCK_NAVIGATION_DATA?.section3.map(({ id, url, icon, popup_items }) => {
        if (popup_items) {
          const tempPopupItems = (popup_items || []).map((i) => ({
            value: i.id,
            label: i.caption,
            icon: i.icon,
            url: i.url,
          }));

          return (
            <Box
              key={id}
              className={clsx(
                'bs-unified__nav-item',
                id === '' && 'bs-unified__nav-item-active',
              )}
              sx={{
                '& .MuiFormControl-root': {
                  minWidth: 'unset',
                },
              }}
            >
              <MenuSelect
                options={tempPopupItems}
                onSelect={() => {}}
                renderElement={
                  <Avatar sx={{ width: 30, height: 30 }} src={url} />
                }
              />
            </Box>
          );
        }

        if (url) {
          return <NavItem key={id} id={icon} active={false} />;
        }

        return null;
      }),
    [],
  );

  const renderSideBar = () => (
    <>
      <HeaderMobile />
      <SideBar
        renderHeader={null}
        renderMenu={<MenuList renderMenuBottom={null} />}
        isMinimal
      >
        <ThemeProvider theme={theme}>
          <NavList renderBottom={renderBottom}>
            {MOCK_NAVIGATION_DATA?.section1?.map(({ id, icon }) => {
              if (id === 'breadstack') {
                return <NavItem key={id} id={id} active />;
              }
              return <NavItem key={id} id={icon} active={false} />;
            })}
          </NavList>
        </ThemeProvider>
      </SideBar>
    </>
  );

  const renderHomeBanner = () => {
    const {
      type,
      message_title,
      message_description,
      banner_color,
      icon,
      dismiss_behaviors,
    } = announcement;
    if (type === ANNOUNCEMENT_TYPE.HOME_PAGE_BANNER)
      return (
        <Stack
          sx={{
            backgroundColor: banner_color,
            px: 3,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            borderRadius: '4px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              fontWeight={700}
              variant='fs16'
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {icon !== 'ch-close-fill' && <Icon name={icon} size={16} unset />}

              {message_title}
            </Typography>
            {dismiss_behaviors !== DISMISS_BEHAVIOR.NON_DISMISSABLE && (
              <Icon size={8} name='close' color={theme.foreground.black} />
            )}
          </Box>
          <Box
            sx={{
              'mt': 0.25,
              '& p': {
                margin: 0,
              },
              '& *': {
                fontFamily: '"Lato", sans-serif',
                lineHeight: '24px',
              },
              '& a': {
                'color': `${theme.palette.primary.main}`,
                '&:visited': {
                  color: `${theme.palette.primary.main}`,
                },
              },
            }}
            className='ql-editor'
            dangerouslySetInnerHTML={{
              __html: message_description,
            }}
          />
        </Stack>
      );
    return null;
  };

  const renderGlobalBanner = () => {
    const { type, message_description, banner_color, icon, dismiss_behaviors } =
      announcement;
    if (type === ANNOUNCEMENT_TYPE.GLOBAL_BANNER) {
      const isLightBackground = banner_color === '#EDF0F7';
      const textColor = isLightBackground ? theme.foreground.black : 'white';

      return (
        <Stack
          sx={{
            backgroundColor: banner_color,
            p: 1,
            pr: 2,
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              alignItems: 'center',
              flex: 1,
              justifyContent: 'center',
            }}
          >
            {icon !== 'ch-close-fill' && <Icon name={icon} size={16} unset />}
            <Box
              sx={{
                'mt': 0.25,
                '& p': {
                  margin: 0,
                },
                '& *': {
                  color: textColor,
                  fontFamily: '"Lato", sans-serif',
                  lineHeight: '24px',
                },
              }}
              className='ql-editor'
              dangerouslySetInnerHTML={{
                __html: message_description,
              }}
            />
          </Box>
          {dismiss_behaviors !== DISMISS_BEHAVIOR.NON_DISMISSABLE && (
            <Icon size={8} name='close' color={textColor} />
          )}
        </Stack>
      );
    }
    return null;
  };

  const renderPopup = () => {
    const { type, message_title, message_description } = announcement;
    if (type === ANNOUNCEMENT_TYPE.POP_UP) {
      return (
        <>
          {/* Backdrop */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
          />
          <Stack
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              borderRadius: '4px',
              minWidth: 400,
              zIndex: 1000,
              color: theme.foreground.black,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2,
                py: 1,
                borderBottom: '1px solid #E2E7F0',
              }}
            >
              <Typography fontWeight={700} variant='fs14'>
                {message_title}
              </Typography>
              <Icon size={8} name='close' />
            </Box>
            <Box
              sx={{
                p: 2,
                pb: 0,
              }}
            >
              <Box
                sx={{
                  '& p': {
                    margin: 0,
                  },
                  '& ol, ul': {
                    paddingLeft: '0 !important',
                  },
                  '& ol > li': {
                    paddingLeft: '1.2em !important',
                  },
                  '& ul > li': {
                    paddingLeft: '0.8em !important',
                  },
                  '& *': {
                    fontFamily: '"Lato", sans-serif',
                    lineHeight: '24px',
                  },
                  '& a': {
                    'color': `${theme.palette.primary.main}`,
                    '&:visited': {
                      color: `${theme.palette.primary.main}`,
                    },
                  },
                }}
                className='ql-editor'
                dangerouslySetInnerHTML={{
                  __html: message_description,
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: 2,
              }}
            >
              <Button>Dismiss</Button>
            </Box>
          </Stack>
        </>
      );
    }
    return null;
  };

  const renderRichPopup = () => {
    const { type, message_title, message_description, image } = announcement;

    // Convert file object to data URL
    let imageUrl = '';
    if (image instanceof File) {
      imageUrl = URL.createObjectURL(image);
    } else if (typeof image === 'string') {
      imageUrl = image;
    }

    if (type === ANNOUNCEMENT_TYPE.RICH_POP_UP) {
      return (
        <>
          {/* Backdrop */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
          />
          <Stack
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              borderRadius: '4px',
              maxWidth: 600,
              zIndex: 1000,
              color: theme.foreground.black,
              p: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
              }}
            >
              <Icon size={8} name='close' />
            </Box>
            {imageUrl && (
              <img
                src={imageUrl}
                alt=''
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  marginTop: '16px',
                  objectFit: 'contain',
                  aspectRatio: 5 / 3,
                }}
              />
            )}
            <Typography
              fontWeight={700}
              variant='fs20'
              sx={{
                mt: 2,
                textAlign: 'center',
              }}
            >
              {message_title}
            </Typography>
            <Box
              sx={{
                py: 2,
              }}
            >
              <Box
                sx={{
                  '& p': {
                    margin: 0,
                  },
                  '& ol, ul': {
                    paddingLeft: '0 !important',
                  },
                  '& ol > li': {
                    paddingLeft: '1.2em !important',
                  },
                  '& ul > li': {
                    paddingLeft: '0.8em !important',
                  },
                  '& *': {
                    fontFamily: '"Lato", sans-serif',
                    lineHeight: '24px',
                  },
                  '& a': {
                    'color': `${theme.palette.primary.main}`,
                    '&:visited': {
                      color: `${theme.palette.primary.main}`,
                    },
                  },
                }}
                className='ql-editor'
                dangerouslySetInnerHTML={{
                  __html: message_description,
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button>Dismiss</Button>
            </Box>
          </Stack>
        </>
      );
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      title='Preview'
      maxWidth='80%'
      onClose={onClose}
      sx={{
        '& .MuiDialogContent-root': {
          p: 0,
        },
      }}
    >
      <Stack
        sx={{
          'background': 'rgba(51, 54, 61, 0.80)',
          'p': 3,
          'position': 'relative',
          '& .bs-unified__app-layout, .bs-unified__nav-list-container': {
            height: '75vh',
          },
        }}
      >
        {renderGlobalBanner()}
        {renderPopup()}
        {renderRichPopup()}
        <AppLayout renderSideBar={renderSideBar()}>
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: theme.background.pageBg,
              height: '75vh',
              overflow: 'auto',
            }}
            className='customized-scrollbar'
          >
            <Stack
              sx={{
                'display': 'flex',
                'flexDirection': 'column',
                'gap': 2,
                'width': '100%',
                'maxWidth': 'min(960px, 70%)',
                'paddingTop': theme.spacing(2),
                'margin': '0px auto',
                '@media screen and (min-width: 1280px)': {
                  paddingBottom: theme.spacing(3.75),
                },
                '@media screen and (max-width: 930px)': {
                  margin: '0px 0px 0px 60px',
                  maxWidth: 'calc(100% - 80px)',
                },
                'color': theme.foreground.black,
              }}
            >
              {renderHomeBanner()}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography
                  color='rgba(51, 54, 61, 0.40)'
                  variant='fs20'
                  fontWeight={700}
                >
                  Hi, welcome to ABC Cannabis
                </Typography>
                <Typography
                  color={theme.palette.primary.main}
                  variant='fs14'
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 0.5,
                  }}
                >
                  <Icon
                    name='external-link'
                    size={16}
                    color={theme.palette.primary.main}
                  />
                  Visit online store
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant='fs16'
                  fontWeight={700}
                  color={theme.foreground.black}
                >
                  Learn more about Breadstack
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',

                    gap: 0.5,
                    p: 3,
                    backgroundColor: '#EDF0F7',
                  }}
                >
                  <Typography variant='fs14' color='rgba(51, 54, 61, 0.40)'>
                    New to Breadstack?
                  </Typography>
                  <Typography variant='fs20' fontWeight={700}>
                    Let’s get you started
                  </Typography>
                  <Typography variant='fs14'>
                    New to Breadstack, or just looking for ways to do more,
                    faster? Here&apos;s what you need to know.
                  </Typography>
                  <Typography variant='fs14'>
                    <Icon name='external-link' size={16} />
                    Learn more
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <img
                    style={{
                      width: '100%',
                      height: '100%',
                    }}
                    src='https://storage.googleapis.com/prd-breadstack-config-files/media/b62ea4c968e8dce49ea9e86c44faa0ba.jpeg'
                    alt=''
                  />
                </Box>
              </Box>
              <Box>
                <Grid container spacing={2} alignItems='stretch'>
                  <Grid
                    item
                    xs={4}
                    sx={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <img
                      style={{
                        aspectRatio: 2 / 1,
                        width: '100%',
                        objectFit: 'cover',
                      }}
                      src='https://storage.googleapis.com/prd-breadstack-config-files/media/adf18170ec89177766eda458154dcc87.jpeg'
                      alt=''
                    />
                    <Box
                      sx={{
                        backgroundColor: '#EDF0F7',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gao: 0.5,
                        flex: 1,
                      }}
                    >
                      <Typography variant='fs14' fontWeight={700}>
                        Filtering customer list for insight
                      </Typography>
                      <Typography variant='fs14'>
                        Segment your customers based on who they are and how
                        they shop. Then, refine and target your marketing and
                        sales efforts.
                      </Typography>
                      <Typography variant='fs14'>
                        <Icon name='external-link' size={16} />
                        Read more
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    sx={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <img
                      style={{
                        aspectRatio: 2 / 1,
                        width: '100%',
                        objectFit: 'cover',
                      }}
                      src='https://storage.googleapis.com/prd-breadstack-config-files/media/25214c1b06d56d5f17c43035b79cf812.jpeg'
                      alt=''
                    />
                    <Box
                      sx={{
                        backgroundColor: '#EDF0F7',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gao: 0.5,
                        flex: 1,
                      }}
                    >
                      <Typography variant='fs14' fontWeight={700}>
                        Setting up chat departments
                      </Typography>
                      <Typography variant='fs14'>
                        Organize your agents into departments so customers can
                        immediately connect with the most knowledgeable support.
                      </Typography>
                      <Typography variant='fs14'>
                        <Icon name='external-link' size={16} />
                        Read more
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </Stack>
        </AppLayout>
      </Stack>
    </Dialog>
  );
};

export default Preview;
